/**
 * Formatting, colours and chart scaffolding shared by the Sales Rep Performance
 * page and its Janus pictures. Pure functions and constants only.
 */

import type { Cell } from '../../lib/types'
import type { Keyed } from '../shared/rows'

export const COLOURS = {
  actual: '#1E5FA8',
  budget: '#9AA5B1',
  py: '#E0A458',
  over: '#2E7D32',
  under: '#B3261E',
} as const

export type Unit = 'gal' | 'fob'

export function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

export function amount(value: number | null, unit: Unit | 'cases'): string {
  if (value === null) return '—'
  const shown = whole.format(Math.abs(value))
  const sign = value < 0 ? '−' : ''
  return unit === 'fob' ? `${sign}$${shown}` : `${sign}${shown}`
}

export function signed(value: number | null, unit: Unit): string {
  if (value === null) return '—'
  if (Math.round(value) === 0) return '0'
  return `${value > 0 ? '+' : '−'}${amount(Math.abs(value), unit)}`
}

export function pct(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`
}

export function tone(value: number | null): string {
  if (value === null || Math.round(value) === 0) return ''
  return value > 0 ? 'srp-over' : 'srp-under'
}

export const UNIT_NAME: Record<Unit, string> = { gal: 'Gallons', fob: 'FOB $' }

/** Sum the comparison columns over rows: the grand total line. */
export function totals(rows: readonly Keyed[]): Record<string, number> {
  const keys = ['month_actual', 'month_py', 'month_budget', 'fytd_actual', 'fytd_py', 'fytd_budget']
  const out: Record<string, number> = {}
  for (const key of keys) out[key] = rows.reduce((sum, row) => sum + (num(row[key]) ?? 0), 0)
  out['month_var_py'] = (out['month_actual'] ?? 0) - (out['month_py'] ?? 0)
  out['month_var_budget'] = (out['month_actual'] ?? 0) - (out['month_budget'] ?? 0)
  out['fytd_var_py'] = (out['fytd_actual'] ?? 0) - (out['fytd_py'] ?? 0)
  out['fytd_var_budget'] = (out['fytd_actual'] ?? 0) - (out['fytd_budget'] ?? 0)
  return out
}

export function ratio(actual: number, against: number): number | null {
  return Math.abs(against) > 1e-9 ? (actual / against) * 100 : null
}

export const STATUS_TONE: Readonly<Record<string, string>> = {
  Reordering: 'srp-over',
  'Awaiting first reorder': 'srp-dim',
  'First reorder overdue': 'srp-under',
  'Reorder overdue': 'srp-under',
  'Reorder booked': 'srp-over',
}

export const STATUS_ORDER = [
  'Reordering',
  'Reorder booked',
  'Awaiting first reorder',
  'Reorder overdue',
  'First reorder overdue',
] as const

/** The size a row is ranked on: the largest of its FYTD actual, prior year and budget. */
export function rankSize(row: Keyed): number {
  return Math.max(
    Math.abs(num(row['fytd_actual']) ?? 0),
    Math.abs(num(row['fytd_py']) ?? 0),
    Math.abs(num(row['fytd_budget']) ?? 0),
  )
}

/**
 * "5227 Mojito Mix" from "5227 - SO Mojito Mix FZN 12pk 30.5oz": the code and the
 * product, without the brand prefix and pack. The full name is the cell's title.
 */
export function shortItem(sku: string | null, item: string | null): string {
  if (item === null) return sku ?? '—'
  const name = item
    .replace(/^\S+\s+-\s+/, '')
    .replace(/^(SOI|SO)\s+/, '')
    .replace(/\s+(FZN|Gln|HGln|Qt)\b.*$/i, '')
  return sku === null ? name : `${sku} ${name}`
}

/**
 * Titles are HTML, never ECharts' own: the shared chart registers no title
 * component, so an option `title` silently drew nothing (seen 2026-10-17). The
 * legend sits at the top of the plot and the grid sizes itself around its
 * labels, so no text can land on an axis.
 */
export const GRID = { left: 8, right: 20, top: 36, bottom: 8, containLabel: true } as const
export const LEGEND_TOP = { top: 0, left: 'center', itemWidth: 14, itemHeight: 10 } as const

export function axisFormatter(unit: Unit) {
  return (value: number) => {
    const abs = Math.abs(value)
    const short =
      abs >= 1e6 ? `${(abs / 1e6).toFixed(1)}M` : abs >= 1e3 ? `${(abs / 1e3).toFixed(0)}K` : String(Math.round(abs))
    return `${value < 0 ? '−' : ''}${unit === 'fob' ? '$' : ''}${short}`
  }
}


/** Where each ship-to stands, and its colour on every picture. */
export const SHIP_TO_STATUS = [
  { key: 'Reordering', colour: '#2E7D32' },
  { key: 'Reordering, some overdue', colour: '#E0A458' },
  { key: 'Reorder booked', colour: '#7CB342' },
  { key: 'Awaiting first reorder', colour: '#6E9BD1' },
  { key: 'No reorder yet, overdue', colour: '#B3261E' },
] as const

export function statusColour(status: string): string {
  return SHIP_TO_STATUS.find((s) => s.key === status)?.colour ?? '#9AA5B1'
}

